import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission } from '@cms/shared';
import { useAuth } from '../lib/auth.js';
import { useHasPermission } from '../lib/permissions.js';

/**
 * Route guards.
 *
 * Worth stating plainly, because it is the mistake this pattern invites: these are UX, not
 * access control. Every route they protect is enforced server-side as well, and the API
 * would refuse the same request from a client that skipped them entirely (§5.2).
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  // The bootstrap refresh has not answered yet. Rendering the login form here would flash
  // it in front of someone who is, in fact, still logged in.
  if (status === 'loading') return <p className="muted">Loading…</p>;

  if (status === 'anonymous') {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { status } = useAuth();
  const allowed = useHasPermission()(permission);

  if (status === 'loading') return <p className="muted">Loading…</p>;
  if (!allowed) {
    return (
      <div className="empty-state">
        <h2>Not available</h2>
        <p className="muted">Your account does not have access to this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Blocks the app until a required password change is done.
 *
 * Sits inside RequireAuth, so it only ever sees an authenticated session. Everything except
 * the change screen itself is unreachable — a forced change that could be clicked past is
 * not a forced change. Logout stays available, because the alternative is trapping someone
 * who cannot remember their current password.
 *
 * As with every guard here, this is UX: the API decides what a session may do, and a client
 * that skipped this would still hold exactly the permissions it held before.
 */
export function RequirePasswordChange({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

/** Any one of these permissions (the funder reports: a Funder's own, or staff's dashboard). */
export function RequireAnyPermission({
  permissions,
  children,
}: {
  permissions: readonly Permission[];
  children: ReactNode;
}) {
  const { status } = useAuth();
  const has = useHasPermission();
  if (status === 'loading') return <p className="muted">Loading…</p>;
  if (!permissions.some((p) => has(p))) {
    return (
      <div className="empty-state">
        <h2>Not available</h2>
        <p className="muted">Your account does not have access to this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Where "/" lands: the dashboard, except for someone who can see only funder reports (slice 13) — a Funder
 * holds no dashboard permission, and landing them on a page they cannot open would greet them with a refusal.
 */
export function HomeRedirect() {
  const has = useHasPermission();
  const { status } = useAuth();
  if (status === 'loading') return <p className="muted">Loading…</p>;
  return (
    <Navigate
      to={!has('dashboard:read') && has('funder:read') ? '/funder-reports' : '/dashboard'}
      replace
    />
  );
}
